import { useContext, useLayoutEffect, useState } from "react";
import { Box, Button, Snackbar, Alert, Typography } from "@mui/material";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import validationSchema from "./Schema";
import {
  apiDeleteRequest,
  apiGetRequest,
  apiPostRequest,
  apiPutRequest,
} from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
import CommunityLinks from "./components/CommunityLinks";
import UploadTOS from "./components/UploadTOS";
import MakeCommunityShine from "./components/MakeCommunityShine";
import UploadCommunityPicture from "./components/UploadCommunityPicture";
import ManageCommunityProjects from "./components/ManageCommunityProjects";
import ContactDetails from "./components/ContactDetails";
import muiStyles from "./styles/muiStyles";
import { useImageUpload } from "../../Components/ImageUploader/useImageUpload";
import { useRevalidateOnLanguageChange } from "../../hooks/useRevalidateOnLanguageChange";
import emitter from "../../Utils/events/eventBus";
import { COMMUNITY_AVATAR_BASE_URL } from "../../Constants";
import { isAvatarFromUiService } from "../../Utils/avatarUtils";
import { AppContext } from "../../Context/AppContext";

export default function CommunitySettingsContainer() {
  const { t } = useTranslation(
    "page-community-settings",
    "validation-error-messages",
  );
  const { formikRef } = useRevalidateOnLanguageChange();

  const [socialPlatformLinks, setSocialPlatformLinks] = useState([]);
  const [submitStatus, setSubmitStatus] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [initialValues, setInitialValues] = useState({
    manager_full_name: "",
    manager_phone_prefix: "+972",
    manager_phone_number: "",
    manager_email: "",
    community_name: "",
    description: "",
    country: "",
    language: "",
    currency: "",
    community_location: "",
    tos_member: "",
    tos_supplier: "",
  });
  const [initialImageUrl, setInitialImageUrl] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [avatarImgUrl, setAvatarImgUrl] = useState("");
  const [imageDeleted, setImageDeleted] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isloading, setIsLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { sidebarData } = useContext(AppContext);
  const isModerator = sidebarData.isModerator;

  const getTags = async () => {
    try {
      const resp = await apiGetRequest(endpoints.communitySettingsTags);
      setTags(resp?.data);
    } catch (ex) {
      console.error("Error getting tags:", ex);
    }
  };

  const getSettings = async () => {
    try {
      setIsLoading(true);
      const resp = await apiGetRequest(endpoints.communitySettings);
      setInitialValues(resp.data);
      setSocialPlatformLinks(resp?.data?.platforms || []);
      setSelectedItems(resp?.data?.tags || []);
      const imageUrl = resp?.data?.image_url;
      const isAvatar = isAvatarFromUiService(
        imageUrl,
        COMMUNITY_AVATAR_BASE_URL,
      );

      if (isAvatar) {
        setAvatarImgUrl(imageUrl);
      } else {
        setImageUrl(imageUrl);
        setInitialImageUrl(imageUrl);
      }
    } catch (ex) {
      console.error("Error fetching settings:", ex);
    } finally {
      setIsLoading(false);
    }
  };

  useLayoutEffect(() => {
    getTags();
    getSettings();
  }, []);

  const { uploadFile } = useImageUpload(
    endpoints.communitySettingsImg,
    (url) => {
      setImageUrl(url);
      setInitialImageUrl(url);
    },
    (message) =>
      setSubmitStatus({
        open: true,
        message: message || t("community_picture.errors.upload_failed"),
        severity: "error",
      }),
  );

  const handleSubmitForm = async (values, { setSubmitting }) => {
    try {
      const {
        community_location,
        community_name,
        description,
        manager_full_name,
        manager_phone_prefix,
        manager_phone_number,
        manager_email,
        tos_member,
        tos_supplier,
      } = values;

      const locationPayload =
        typeof community_location === "string"
          ? { community_location }
          : Object.fromEntries(
              Object.entries(community_location || {}).filter(
                ([key]) => key !== "address",
              ),
            );

      setIsLoading(true);

      const imageUploadPromise = tempImageFile
        ? new Promise((resolve, reject) => {
            uploadFile(tempImageFile)
              .then((res) => {
                const imageUrl = URL.createObjectURL(tempImageFile);
                emitter.emit("community-image-updated", imageUrl);
                resolve(res);
              })
              .catch((error) => {
                reject(new Error(t("alerts.image_upload_failed")));
              });
          })
        : Promise.resolve(null);

      const imageDeletePromise =
        imageDeleted && initialImageUrl && !tempImageFile
          ? new Promise((resolve, reject) => {
              apiDeleteRequest(endpoints.communitySettingsImg)
                .then((res) => {
                  setInitialImageUrl(null);
                  resolve(res);
                })
                .catch((error) => {
                  reject(new Error(t("alerts.image_delete_failed")));
                });
            })
          : Promise.resolve(null);

      const updateCommunityInfoPromise = apiPutRequest(
        endpoints.updateCommunityInfo,
        {
          community_name,
          description: description || "",
          tags: selectedItems,
          ...locationPayload,
        },
      ).then((res) => {
        emitter.emit("community-name-updated", community_name);
        return res;
      });

      const results = await Promise.allSettled(
        [
          imageUploadPromise,
          imageDeletePromise,
          updateCommunityInfoPromise,
          apiPutRequest(endpoints.updateCommunityManagerInfo, {
            manager_full_name: manager_full_name,
            manager_phone_prefix: manager_phone_prefix,
            manager_phone_number: manager_phone_number,
            manager_email: manager_email,
          }),
          apiPutRequest(
            endpoints.updateCommunityPlatforms,
            socialPlatformLinks.map(({ type, link }) => ({
              platform_type: type,
              link: link,
            })),
          ),
          tos_member
            ? apiPostRequest(endpoints.updateCommunityTOSMember, {
                tos: tos_member,
              })
            : apiDeleteRequest(endpoints.updateCommunityTOSMember),
          tos_supplier
            ? apiPostRequest(endpoints.updateCommunityTOSSupplier, {
                tos: tos_supplier,
              })
            : apiDeleteRequest(endpoints.updateCommunityTOSSupplier),
        ].filter(Boolean),
      );

      if (!imageUrl && !tempImageFile) {
        emitter.emit("community-image-updated", avatarImgUrl);
      }

      emitter.emit("fetch-get-started-content");

      const hasErrors = results.some((res) => res.status === "rejected");

      if (hasErrors) {
        throw new Error(t("alerts.error"));
      }

      setSubmitStatus({
        open: true,
        message: t("alerts.success"),
        severity: "success",
      });
    } catch (error) {
      setSubmitStatus({
        open: true,
        message: error.message || t("alerts.error"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const {
    saveButtonWrapperStyles,
    saveChangesButtonStyles,
    moderatorTextStyles,
  } = muiStyles;

  const disableFields = isloading || isModerator;

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema(t)}
      onSubmit={handleSubmitForm}
      enableReinitialize
      validateOnBlur={true}
      validateOnChange={false}
      validateOnMount={false}
    >
      {({ errors, values, handleChange, handleSubmit, isSubmitting }) => {
        const hasErrors = Object.keys(errors).length > 0;

        return (
          <form
            onSubmit={(e) => {
              setIsSubmitted(true);
              handleSubmit(e);
            }}
          >
            <Box
              sx={{
                width: "100%",
                backgroundColor: "#f8f7fa",
                marginTop: {
                  xs: "75px",
                  md: "0px",
                },
              }}
            >
              {isModerator && (
                <Typography sx={moderatorTextStyles}>
                  {t("moderator_sub_heading")}
                </Typography>
              )}
              <ContactDetails disableFields={disableFields} />

              <MakeCommunityShine
                {...{
                  disableFields,
                  tags,
                  selectedItems,
                  setSelectedItems,
                }}
              />

              <UploadCommunityPicture
                {...{
                  imageUrl,
                  setImageUrl,
                  tempImageFile,
                  setTempImageFile,
                  setSubmitStatus,
                  disableFields,
                  setImageDeleted,
                  initialImageUrl,
                  avatarImgUrl,
                  setAvatarImgUrl,
                }}
              />

              <ManageCommunityProjects />

              <CommunityLinks
                {...{
                  socialPlatformLinks,
                  setSocialPlatformLinks,
                  disableFields,
                }}
              />

              <UploadTOS {...{ values, handleChange, disableFields }} />

              {isSubmitted && hasErrors && (
                <Alert severity="error" sx={{ mt: "36px" }}>
                  {t("alerts.form_incomplete")}
                </Alert>
              )}

              <Box sx={saveButtonWrapperStyles}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || disableFields}
                  sx={saveChangesButtonStyles}
                >
                  {t("save_changes")}
                </Button>
              </Box>

              <Snackbar
                open={submitStatus.open}
                autoHideDuration={6000}
                onClose={() =>
                  setSubmitStatus((prev) => ({ ...prev, open: false }))
                }
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <Alert
                  onClose={() =>
                    setSubmitStatus((prev) => ({ ...prev, open: false }))
                  }
                  severity={submitStatus.severity}
                  sx={{ width: "100%" }}
                >
                  {submitStatus.message}
                </Alert>
              </Snackbar>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
}
